import React, { useEffect, useMemo, useState } from "react";
import TableChart, { type TableChartRowData } from "../TableChart";
import FindingsBySeverityLegend from "../FindingsBySeverity/FindingsSeverityLegend";
import useSetDataWithExport from "../useSetDataWithExport";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import FindingsBySeverityChart from "../FindingsBySeverity/FindingsBySeverityChart";
import "./TopFeatureTable.scss";
import {
  Tooltip,
  useIcon,
  TableCell,
  tableCellTypesEnum,
  formatNumber,
} from "@SeemplicityDev/shared";
import EntityList, {
  EntityItem,
} from "../../../../Components/EntityLogo/EntityList";

export interface SourceInfo {
  id: string;
  name: string;
  display_name?: string;
  [key: string]: unknown;
}

export interface CountBySeverity {
  total: number;
  [key: string]: number;
}

export interface BaseTopFeatureRow {
  total_count: number;
  count_by_severity: CountBySeverity;
  sources: SourceInfo[];
}

export interface GenericWidgetQueryResult<T extends BaseTopFeatureRow> {
  generic_widget_data: {
    metadata?: string;
    data: (Omit<T, "sources" | "count_by_severity"> & {
      sources: string[];
      count_by_severity: string;
    })[];
  } | null;
}

export interface WidgetTableProps {
  selectedScope?: { value?: { id?: string } } | null;
  filterToExecute: string | null;
  scopeToExecute: string | null;
  id: string;
  isForExport?: boolean;
  setTotalWidgets?: ((total: number) => void) | null;
  index?: number | null;
  setIsReadyToExport?: ((ready: boolean) => void) | null;
}

interface TopFeatureTableProps<T extends BaseTopFeatureRow> {
  columns: ColumnDef<T, any>[];
  data: GenericWidgetQueryResult<T> | null | undefined;
  loading: boolean;
  error: unknown;
  refetch: () => void;
  index: number | null;
  isForExport: boolean;
  setTotalWidgets: ((total: number) => void) | null;
  onChartClick: (severity: string, row: T) => void;
  id: string;
  identifier: Extract<keyof T, string>;
  setIsReadyToExport?: ((ready: boolean) => void) | null;
}

const TopFeatureTable = <T extends BaseTopFeatureRow>({
  columns,
  data,
  loading,
  error,
  refetch,
  index,
  isForExport,
  setTotalWidgets,
  onChartClick,
  id,
  identifier,
  setIsReadyToExport = null,
}: TopFeatureTableProps<T>) => {
  const [tableData, setTableData] = useState<T[]>([]);
  const [maxFindings, setMaxFindings] = useState(0);
  const { setDataWithExport, setLoadedRowsCount } = useSetDataWithExport({
    isForExport,
    index,
    setTotalWidgets,
    setData: setTableData,
    data: tableData,
    error,
    loading,
    setIsReadyToExport,
  });

  const { getDataSourceIcon } = useIcon(isForExport ? "LIGHT" : undefined);

  const updatedData = (rows: T[]): T[] => {
    return rows?.map((item) => {
      const hasWiz = item.sources.some(
        (source) => source.name.toLowerCase() === "wiz"
      );

      return {
        ...item,
        sources: item.sources
          ?.filter(
            (source) =>
              !(hasWiz && source.name.toLowerCase() === "wiz vulnerabilities")
          )
          ?.map((source) =>
            source.name.toLowerCase() === "wiz vulnerabilities"
              ? { ...source, name: "Wiz" }
              : source
          ),
      };
    });
  };

  useEffect(() => {
    if (!!data) {
      const metadata = JSON.parse(data.generic_widget_data?.metadata ?? "{}");
      const sourcesMetadata: SourceInfo[] | undefined =
        metadata?.datasource_definition_attributes;
      const rowsData = data?.generic_widget_data?.data?.map((row) => {
        const sources = row.sources.map((s: string) =>
          sourcesMetadata?.find(
            (currentSource: SourceInfo) => currentSource.id === s
          )
        );
        const countBySeverity = JSON.parse(row.count_by_severity ?? "{}");
        return {
          ...row,
          sources,
          count_by_severity: { ...countBySeverity, total: row.total_count },
        } as T;
      });

      /* ------------------------------------------------ */
      /* MERGE WIZ & WIZ VULN - DELETE ASAP               */
      /* ------------------------------------------------ */
      const manipulatedData = updatedData(rowsData ?? []);
      /* ------------------------------------------------ */
      const max = Math.max(...manipulatedData?.map((row) => row.total_count));

      setDataWithExport(manipulatedData);
      setMaxFindings(max);
    }
  }, [data]);

  const columnHelper = createColumnHelper<T>();

  const tableColumns = useMemo(() => {
    return [
      ...columns,
      columnHelper.accessor((row: T) => row.total_count, {
        id: "total_count",
        cell: (props) => {
          return (
            <TableCell type={tableCellTypesEnum.COMPONENT}>
              <div
                className={`findings-wrapper ${isForExport ? "exported" : ""}`}
              >
                <Tooltip
                  content={formatNumber(props.getValue() ?? 0) || undefined}
                >
                  <span className={"findings-count"}>
                    {formatNumber(props.getValue() ?? 0)}
                  </span>
                </Tooltip>
                <FindingsBySeverityChart
                  loading={loading}
                  // `error` is the untyped (unknown) hook error; the chart prop
                  // is string|boolean|null but only checks truthiness.
                  error={error as string | boolean | null | undefined}
                  findingCounts={props.row.original?.count_by_severity}
                  namePrefix={
                    props.row?.original?.[identifier as keyof T] as string
                  }
                  filterFieldValue={
                    props.row?.original?.[identifier as keyof T] as
                      | string
                      | undefined
                  }
                  maxFindings={maxFindings}
                  widgetId={id}
                  onChartClick={(severity?: string) =>
                    onChartClick(severity as string, { ...props.row?.original })
                  }
                  isForExport={isForExport}
                  setLoaded={() =>
                    setLoadedRowsCount((prev: number) => prev + 1)
                  }
                />
              </div>
            </TableCell>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        header: "Findings",
        minSize: 200,
        size: 350,
      }),
      columnHelper.accessor((row: T) => row.sources, {
        id: "sources",
        cell: (props) => {
          const sourcesList: EntityItem[] = props.getValue()?.map((source) => ({
            logo: getDataSourceIcon(source?.name ?? "", "S") as string,
            name: source?.display_name,
          }));

          return (
            <TableCell type={tableCellTypesEnum.COMPONENT}>
              <EntityList
                list={sourcesList}
                maxToDisplay={!isForExport ? 3 : undefined}
                className={isForExport ? "wrapped-logos-list" : ""}
              />
            </TableCell>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        header: "Sources",
      }),
    ];
  }, [tableData, identifier, isForExport, getDataSourceIcon]);

  return (
    <TableChart
      className={"top-feature-table"}
      // Column defs and rows are typed against the generic T; TableChart's props
      // use the open TableChartRowData shape. Bridge casts (same pattern as
      // FindingsByDataSourceAndSeverityTable) — runtime data is unchanged.
      columns={
        tableColumns as unknown as ColumnDef<TableChartRowData, unknown>[]
      }
      tableData={tableData as unknown as TableChartRowData[]}
      isLoading={loading}
      error={error}
      onRefetch={refetch}
      isForExport={isForExport}
      legend={<FindingsBySeverityLegend />}
      noSelectedLoaderTitle={undefined}
    />
  );
};

export default TopFeatureTable;
